/**
 * Build a {@link ProxyHandler} that logs every interaction with the target:
 * property reads/writes/deletes, descriptor and prototype access, `in`
 * checks, key enumeration, extensibility changes, and direct calls. Function
 * properties accessed via `get` are themselves wrapped so each method call is
 * logged with its arguments.
 *
 * Non-mutating operations (reads, descriptor lookups, calls) log at `debug`;
 * mutating operations (writes, deletes, `defineProperty`, `setPrototypeOf`,
 * `preventExtensions`) log at `info`.
 *
 * @param logger - Receiver of the log records.
 * @param name - Identifier of the wrapped object, included in every record.
 * @param scope - Higher-level grouping (e.g. module name), included in every record.
 *
 * @example
 * ```typescript
 * const handler = createLogProxyHandler(logger, 'cache', 'storage');
 * const observed = new Proxy({get(key: string) { ... }}, handler);
 * observed.get('foo'); // logs: called get cache[get], then called cache.get(...)
 * 'foo' in observed;   // logs: called has cache[foo]
 * delete observed.foo; // logs: called delete cache[foo]
 * ```
 */
export const createLogProxyHandler = (logger, name, scope) => {
    const methodCache = new Map();
    return {
        get: (target, p, receiver) => {
            const value = Reflect.get(target, p, receiver);
            logger.debug({ scope, name, prop: p, value }, `called get ${name}[${String(p)}]`);
            if (typeof value === 'function') {
                const fn = value;
                const entry = methodCache.get(p);
                // Return the cached wrapper only when it still wraps the current
                // function.  If the property was reassigned (proxy.fn = newFn)
                // the identity check fails and we build a fresh wrapper below.
                if (entry !== undefined && entry.fn === fn) {
                    return entry.wrapper;
                }
                const wrapper = new Proxy(fn, {
                    apply: (targetFn, thisArg, args) => {
                        logger.debug({ scope, name, method: String(p), args }, `called ${name}.${String(p)}(...)`);
                        const ctx = thisArg === receiver ? target : thisArg;
                        return Reflect.apply(targetFn, ctx, args);
                    },
                });
                methodCache.set(p, { fn, wrapper });
                return wrapper;
            }
            return value;
        },
        set: (target, p, value, receiver) => {
            logger.info({ scope, name, prop: p, value }, `called set ${name}[${String(p)}]`);
            return Reflect.set(target, p, value, receiver);
        },
        has: (target, p) => {
            const result = Reflect.has(target, p);
            logger.debug({ scope, name, prop: p, value: result }, `called has ${name}[${String(p)}]`);
            return result;
        },
        deleteProperty: (target, p) => {
            logger.info({ scope, name, prop: p }, `called delete ${name}[${String(p)}]`);
            return Reflect.deleteProperty(target, p);
        },
        defineProperty: (target, p, descriptor) => {
            logger.info({ scope, name, prop: p, descriptor }, `called defineProperty ${name}[${String(p)}]`);
            return Reflect.defineProperty(target, p, descriptor);
        },
        getOwnPropertyDescriptor: (target, p) => {
            const descriptor = Reflect.getOwnPropertyDescriptor(target, p);
            logger.debug({ scope, name, prop: p, descriptor }, `called getOwnPropertyDescriptor ${name}[${String(p)}]`);
            return descriptor;
        },
        ownKeys: target => {
            const keys = Reflect.ownKeys(target);
            logger.debug({ scope, name, keys }, `called ownKeys ${name}`);
            return keys;
        },
        getPrototypeOf: target => {
            const prototype = Reflect.getPrototypeOf(target);
            logger.debug({ scope, name, prototype }, `called getPrototypeOf ${name}`);
            return prototype;
        },
        setPrototypeOf: (target, prototype) => {
            logger.info({ scope, name, prototype }, `called setPrototypeOf ${name}`);
            return Reflect.setPrototypeOf(target, prototype);
        },
        isExtensible: target => {
            const extensible = Reflect.isExtensible(target);
            logger.debug({ scope, name, extensible }, `called isExtensible ${name}`);
            return extensible;
        },
        preventExtensions: target => {
            logger.info({ scope, name }, `called preventExtensions ${name}`);
            return Reflect.preventExtensions(target);
        },
        apply: (target, thisArg, args) => {
            logger.debug({ scope, name, args }, `called ${name}(...)`);
            return Reflect.apply(target, thisArg, args);
        },
        construct: (target, args, newTarget) => {
            logger.debug({ scope, name, args }, `called new ${name}(...)`);
            return Reflect.construct(target, args, newTarget);
        },
    };
};
/**
 * Curried helper that wraps an object in a logging proxy. Useful when several
 * objects share the same logger and scope and only differ by name.
 *
 * @param logger - Receiver of the log records.
 * @param scope - Higher-level grouping shared by every object produced by the returned function.
 *
 * @example
 * ```typescript
 * const wrap = proxyWithLog(logger, 'media');
 *
 * const trackedCamera = wrap(camera, 'Camera');
 * const trackedMic = wrap(microphone, 'Microphone');
 * ```
 */
export const proxyWithLog = (logger, scope) => (obj, name) => {
    return new Proxy(obj, createLogProxyHandler(logger, name, scope));
};
//# sourceMappingURL=proxyLog.js.map